import { apiClient } from '../../../api/client'
import { downloadXlsx } from '../../../lib/download'
import { CLASSIFICATION_LOOKUP_CONFIG } from '../config'
import type {
  ClassificationLookup,
  ClassificationLookupKind,
  ClassificationLookupPage,
  ClassificationLookupStatus,
  ClassificationLookupUpsert,
} from '../types'

/**
 * Stratejik Hedefler / Katkılar yönetim uçları (127). İki kataloğun uçları birebir aynıdır; taban yol
 * türden seçilir (`config.ts`). Etkinlik Türü'nün `eventTypeApi` deseniyle aynı.
 */
const base = (kind: ClassificationLookupKind) => CLASSIFICATION_LOOKUP_CONFIG[kind].apiBase

/**
 * Silinmemiş kayıtların (sıralı) listesi. Lookup ölçeği küçük → tümünü tek sayfada çekeriz (size=200).
 * Arama (`q`) yok: liste sıra düzeninde, tek istekte gelir.
 */
export async function getClassificationLookups(
  kind: ClassificationLookupKind,
): Promise<ClassificationLookupPage> {
  const { data } = await apiClient.get<ClassificationLookupPage>(base(kind), {
    params: { page: 0, size: 200 },
  })
  return data
}

/** Yeni kayıt oluştur (201 → oluşturulan kayıt; sıra sona atanır). */
export async function createClassificationLookup(
  kind: ClassificationLookupKind,
  body: ClassificationLookupUpsert,
): Promise<ClassificationLookup> {
  const { data } = await apiClient.post<ClassificationLookup>(base(kind), body)
  return data
}

/** Kaydı güncelle (adlar; optimistic lock için modDate zorunlu). */
export async function updateClassificationLookup(
  kind: ClassificationLookupKind,
  id: number,
  body: ClassificationLookupUpsert,
): Promise<ClassificationLookup> {
  const { data } = await apiClient.put<ClassificationLookup>(`${base(kind)}/${id}`, body)
  return data
}

/** Durum değiştir (Aktif/Pasif; yalnız 0↔1). */
export async function changeClassificationLookupStatus(
  kind: ClassificationLookupKind,
  id: number,
  body: ClassificationLookupStatus,
): Promise<ClassificationLookup> {
  const { data } = await apiClient.put<ClassificationLookup>(`${base(kind)}/${id}/status`, body)
  return data
}

/** Soft-delete (record_status=2). 204. */
export async function deleteClassificationLookup(
  kind: ClassificationLookupKind,
  id: number,
): Promise<void> {
  await apiClient.delete(`${base(kind)}/${id}`)
}

/**
 * Yeniden sıralama — ekranda görünen tüm (silinmemiş) kayıtların id sırası. Backend bu sırayı 1..N
 * SORT_INDEX olarak yeniden yazar (pairwise takas değil; eksik liste reddedilir). 204.
 */
export async function reorderClassificationLookups(
  kind: ClassificationLookupKind,
  orderedIds: number[],
): Promise<void> {
  await apiClient.put(`${base(kind)}/reorder`, { orderedIds })
}

/**
 * Listeyi Excel (.xlsx) olarak indirir. Sunucu tüm silinmemiş kayıtları sıra düzeninde döndürür; dosya
 * adı yanıttaki Content-Disposition'dan okunur, yoksa türün yedek adına düşülür ({@link downloadXlsx}).
 */
export async function exportClassificationLookups(kind: ClassificationLookupKind): Promise<void> {
  await downloadXlsx(
    `${base(kind)}/export`,
    {},
    `${CLASSIFICATION_LOOKUP_CONFIG[kind].exportName}.xlsx`,
  )
}
